import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ArrowClockwiseRegular } from "@fluentui/react-icons";
import { Button } from "@fluentui/react-components";
import { LoadingPage } from "Components/Pages/LoadingPage";
import { LogCategory } from "Enumerations";
import { M365Alert } from "Shared/Components/Alert/M365Alert";
import { UnauthenticatedPage } from "Components/Pages/UnauthenticatedPage";
import { useLogging } from "Shared/Hooks";

import {
    ChunkLoadRecoveryDecision,
    ChunkLoadRecoveryReason,
    getChunkLoadFailure,
    getChunkLoadRecoveryDecision,
    isChunkReloadPending,
    reloadForChunkLoadFailure
} from "./ChunkLoadRecovery";

const chunkLoadTagId = "7f44a56f-1152-4f01-b1ce-02f9bfe02379";
const chunkLoadEventName = "ChunkLoadRecovery";

const getInitialDecision = (): ChunkLoadRecoveryDecision | undefined => (isChunkReloadPending() ? undefined : getChunkLoadRecoveryDecision());

const offlineMessage = "Part of M365 Pulse couldn't load because you seem to be offline. Reconnect, then reload the page.";
const staleVersionMessage =
    "Part of M365 Pulse couldn't load. This usually happens when a new version is released while the page is open. Reload the page to get the latest version.";

const getMessage = (reason: ChunkLoadRecoveryReason) => (reason === "Offline" ? offlineMessage : staleVersionMessage);

interface ChunkLoadErrorPageProps {
    error: unknown;
    correlationId: string;
}

/**
 * Shown by the error boundary when a lazy-loaded chunk fails. Reloads the page up to MAX_CHUNK_RELOADS times to pick up the
 * latest deployment, then shows a recoverable error with a Reload button if the chunk still fails.
 */
export const ChunkLoadErrorPage = ({ error, correlationId }: ChunkLoadErrorPageProps) => {
    const { trackEvent } = useLogging(LogCategory.Core);
    const [decision] = useState(getInitialDecision);
    const [reloadFailed, setReloadFailed] = useState(false);

    const eventProperties = useMemo(() => {
        const failure = getChunkLoadFailure(error);
        return {
            ChunkType: failure?.kind ?? "",
            ChunkRequest: failure?.request ?? "",
            FailureType: failure?.failureType ?? "",
            CorrelationId: correlationId
        };
    }, [correlationId, error]);

    useEffect(() => {
        if (!decision) {
            return;
        }
        trackEvent(chunkLoadTagId, chunkLoadEventName, {
            ...eventProperties,
            Action: decision.reload ? "Reload" : "ShowError",
            Reason: decision.reason,
            LastReloadAgeMs: decision.lastReloadAgeMs === undefined ? "" : String(decision.lastReloadAgeMs),
            RecentReloadCount: decision.recentReloadCount === undefined ? "" : String(decision.recentReloadCount)
        });
        if (decision.reload && !reloadForChunkLoadFailure()) {
            setReloadFailed(true);
        }
    }, [decision, eventProperties, trackEvent]);

    const reload = useCallback(() => {
        trackEvent(chunkLoadTagId, chunkLoadEventName, { ...eventProperties, Action: "ManualReload" });
        if (!reloadForChunkLoadFailure()) {
            window.location.reload();
        }
    }, [eventProperties, trackEvent]);

    if (!decision || (decision.reload && !reloadFailed)) {
        return <LoadingPage label="Loading the latest version of M365 Pulse..." />;
    }

    return (
        <UnauthenticatedPage
            content={
                <M365Alert
                    intent="error"
                    correlationId={correlationId}
                    message={getMessage(reloadFailed ? "StorageUnavailable" : decision.reason)}
                    action={
                        <Button icon={<ArrowClockwiseRegular />} onClick={reload}>
                            Reload
                        </Button>
                    }
                />
            }
        />
    );
};
